import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ImagePlus, Video, X, Loader2, Megaphone, Clock, Pencil, Search, Plus, ShieldCheck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { EditAnnonceModal } from "@/components/EditAnnonceModal";
import { VerificationFaciale } from "@/components/VerificationFaciale";
import { CATEGORIES } from "@/lib/categories";
import { AIR_BENGUE } from "@/lib/airBengue";
import { getUser, formatFCFA, formatDate } from "@/lib/user";
import { useApercu } from "@/lib/apercu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PALIER_LABELS = { gratuit: "Gratuit", jour: "24 heures", semaine: "Semaine", mois: "Mois" };

const mediaUrl = (u) => {
  if (!u.startsWith("/api")) return u;
  const user = getUser();
  return `${process.env.REACT_APP_BACKEND_URL}${u}${user ? `?viewer_id=${user.id}` : ""}`;
};

export default function Annonces() {
  const user = getUser();
  const [params] = useSearchParams();
  const [tarifs, setTarifs] = useState(null);
  const [categorie, setCategorie] = useState(
    CATEGORIES[params.get("cat")] ? params.get("cat") : user?.category || "lago"
  );
  const [palier, setPalier] = useState("gratuit");
  const [texte, setTexte] = useState("");
  const [nomAnnonceur, setNomAnnonceur] = useState(user?.firstname || "");
  const [telephones, setTelephones] = useState([
    String(user?.contact || "").replace(/\D/g, "").slice(-10),
  ]);
  const [photos, setPhotos] = useState([]);
  const [video, setVideo] = useState(null);
  const [editAnnonce, setEditAnnonce] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [etatVisage, setEtatVisage] = useState(null);
  const apercu = useApercu();
  const [controleVisage, setControleVisage] = useState(null);
  const [annonces, setAnnonces] = useState([]);
  const photoInput = useRef(null);
  const videoInput = useRef(null);

  const refresh = useCallback(() => {
    if (user) axios.get(`${API}/annonces`, { params: { profile_id: user.id } }).then((r) => setAnnonces(r.data));
  }, [user?.id]);

  useEffect(() => {
    axios.get(`${API}/annonces/tarifs`).then((r) => setTarifs(r.data));
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/verification-faciale/etat`, { params: { profile_id: user.id, categorie } })
      .then((r) => setEtatVisage(r.data))
      .catch(() => setEtatVisage(null));
  }, [categorie, user?.id]);

  useEffect(() => {
    if (tarifs && !tarifs[categorie][palier]) setPalier(Object.keys(tarifs[categorie])[0]);
    setTexte("");
    setPhotos([]);
    setVideo(null);
  }, [categorie, tarifs]);

  const offre = tarifs?.[categorie]?.[palier];

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await axios.post(`${API}/annonces/upload`, fd);
    return res.data;
  };

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !offre) return;
    if (photos.length + files.length > offre.photosMax) {
      toast.error(`Maximum ${offre.photosMax} photo(s) pour ce palier.`);
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        const media = await uploadFile(f);
        setPhotos((prev) => [...prev, media]);
      }
    } catch {
      toast.error("Échec de l'upload d'une photo.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !offre) return;
    setUploading(true);
    try {
      const duree = await new Promise((resolve) => {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => resolve(v.duration);
        v.onerror = () => resolve(0);
        v.src = URL.createObjectURL(file);
      });
      if (offre.videoDureeSecondesMax && duree > offre.videoDureeSecondesMax) {
        toast.error(`La vidéo ne doit pas dépasser ${offre.videoDureeSecondesMax} secondes.`);
        return;
      }
      const media = await uploadFile(file);
      setVideo({ ...media, duree_secondes: duree });
    } catch {
      toast.error("Échec de l'upload de la vidéo.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deposer = async () => {
    if (!photos.length) {
      toast.error("Ajoutez au moins une photo.");
      return;
    }
    if (!nomAnnonceur.trim()) {
      toast.error("Indiquez le nom à afficher sur l'annonce.");
      return;
    }
    // Air-Bengue Europe : les annonceurs sont à l'étranger, leur numéro est international
    // (« +32 470 11 22 33 »). On garde le « + » et on accepte 8 à 15 chiffres.
    const international = categorie === "air";
    const numeros = telephones
      .map((t) => (international ? t.replace(/[^\d+]/g, "") : t.replace(/\D/g, "")))
      .filter(Boolean);
    const invalide = international
      ? numeros.some((n) => n.replace(/\D/g, "").length < 8 || n.replace(/\D/g, "").length > 15)
      : numeros.some((n) => n.length !== 10);
    if (!numeros.length || invalide) {
      toast.error("Numéro de contact invalide", {
        description: international
          ? "Indiquez le numéro avec son indicatif pays (ex. +32 470 11 22 33)."
          : "Chaque numéro doit comporter 10 chiffres (ex. 07 07 07 07 07).",
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/annonces/deposer`, {
        profile_id: user.id, categorie, palier, texte, photos, video,
        nom_annonceur: nomAnnonceur.trim(), telephones: numeros,
      });
      if (res.data.payment_url) {
        localStorage.setItem("fodindin_transaction", res.data.transaction_id);
        window.location.href = res.data.payment_url;
        return;
      }
      toast.success(res.data.message, {
        description: `Expire le ${formatDate(res.data.date_expiration)}`,
      });
      setTexte("");
      setPhotos([]);
      setVideo(null);
      refresh();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "VERIFICATION_FACIALE_REQUISE") {
        // Contrôle du visage demandé aux ANNONCEURS de cette rubrique très demandée.
        setControleVisage({ photo_reference: d.photo_reference || photos[0]?.url });
        return;
      }
      toast.error(typeof d === "string" ? d : d?.message || "Erreur lors du dépôt.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="bg-[#FAF8F5] min-h-screen">
        <Navbar />
        <main className="max-w-xl mx-auto px-4 pt-40 pb-24 text-center">
          <Lock size={44} className="mx-auto text-[#E05638] mb-6" />
          <h1 className="font-heading text-3xl font-extrabold text-stone-900">Créez d'abord votre profil</h1>
          <p className="text-stone-600 mt-4">Le dépôt d'annonce est lié à votre profil FodindinCa.</p>
          <Link to="/inscription" data-testid="annonce-register-link" className="mt-8 inline-block bg-[#E05638] hover:bg-[#C84428] text-white font-semibold px-8 py-4 rounded-full transition-colors">
            Créer mon profil gratuit
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const cat = CATEGORIES[categorie];

  return (
    <div className="bg-[#FAF8F5] min-h-screen">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="text-[#E05638] text-sm font-semibold tracking-[0.25em] uppercase">Dépôt d'annonce</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-stone-900 mt-3">
            Publiez vos photos, {user.firstname}
          </h1>
          <p className="text-stone-600 mt-4 max-w-2xl leading-relaxed">
            Chaque catégorie a sa grille : un même média ne peut jamais apparaître dans deux catégories différentes.
          </p>
          {/* Retour terrain : un simple visiteur n'a rien à déposer — sortie claire vers la consultation */}
          <div
            className="mt-6 flex flex-wrap items-center justify-between gap-3 bg-white border border-[#E5DFD5] rounded-2xl px-5 py-4"
            data-testid="annonces-bandeau-consulter"
          >
            <p className="text-sm text-stone-600">
              Vous voulez seulement <strong>regarder les annonces</strong> ? Le dépôt n'est pas obligatoire.
            </p>
            <Link
              to={`/categorie/${categorie}/annonces`}
              data-testid="annonces-lien-consulter"
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#0A5C36] hover:bg-[#084027] rounded-full px-4 py-2 transition-colors"
            >
              <Search size={14} /> Voir les annonces
            </Link>
          </div>
          {apercu.acces?.[categorie] && (
            <div className="mt-6 rounded-2xl border border-[#0A5C36]/30 bg-[#0A5C36]/5 px-5 py-4"
              data-testid="annonces-bandeau-abonnement-actif">
              <p className="inline-flex items-center gap-2 text-sm font-bold text-[#0A5C36]">
                <ShieldCheck size={15} /> Votre abonnement {cat?.name} est déjà actif — aucun
                second paiement d'abonnement n'est demandé ici.
              </p>
              <p className="mt-1 text-xs text-stone-600">
                Il ne reste qu'à publier : choisissez la durée d'affichage de votre annonce
                ci-dessous (la formule gratuite est disponible), ajoutez vos photos puis validez.
              </p>
            </div>
          )}
          {etatVisage?.certifie && (
            <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0A5C36]/10 px-4 py-2 text-xs font-bold text-[#0A5C36]"
              data-testid="annonces-badge-certifie">
              <ShieldCheck size={14} /> Profil certifié — votre badge s'affiche sur vos annonces
            </p>
          )}
          {etatVisage?.obligatoire && (
            <div className="mt-4 rounded-2xl border border-[#0A5C36]/30 bg-[#0A5C36]/5 px-5 py-4"
              data-testid="annonces-bandeau-verification">
              <p className="text-sm font-semibold text-[#0A5C36]">
                Rubrique très demandée ({etatVisage.annonces_actives} annonces) : un selfie de
                contrôle est demandé aux annonceurs avant publication.
              </p>
              <p className="mt-1 text-xs text-stone-600">
                Ce contrôle ne concerne que les dépôts — jamais les visiteurs qui consultent les annonces.
              </p>
            </div>
          )}
        </motion.div>

        {/* catégorie */}
        <div className="mt-10 flex flex-wrap gap-3">
          {Object.values(CATEGORIES).map((c) => (
            <button
              key={c.id}
              onClick={() => setCategorie(c.id)}
              data-testid={`annonce-cat-${c.id}`}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold border-2 transition-all ${categorie === c.id ? "text-white border-transparent" : "bg-white text-stone-600 border-[#E5DFD5]"}`}
              style={categorie === c.id ? { backgroundColor: c.color } : {}}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* paliers */}
        {tarifs && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.entries(tarifs[categorie]).map(([p, o]) => (
              <button
                key={p}
                onClick={() => setPalier(p)}
                data-testid={`annonce-palier-${p}`}
                className={`text-left bg-white rounded-2xl border-2 p-5 transition-all ${palier === p ? "shadow-md" : "border-[#E5DFD5]"}`}
                style={palier === p ? { borderColor: cat.color } : {}}
              >
                <div className="font-heading font-bold text-stone-900">{PALIER_LABELS[p]}</div>
                <div className="font-heading text-xl font-extrabold mt-1" style={{ color: cat.color }}>
                  {o.prix === 0 ? "0 FCFA" : formatFCFA(o.prix)}
                </div>
                <div className="text-xs text-stone-500 mt-2 leading-relaxed">
                  {o.photosMax} photo{o.photosMax > 1 ? "s" : ""}
                  {o.videoMax > 0 && ` + ${o.videoMax} vidéo (≤${o.videoDureeSecondesMax}s)`}
                  {" · "}{o.dureeJours} jours
                </div>
              </button>
            ))}
          </div>
        )}

        {/* uploads */}
        {offre && (
          <div className="mt-8 bg-white border border-[#E5DFD5] rounded-2xl p-6 space-y-5">
            <div>
              <h3 className="font-heading font-bold text-stone-900 mb-3">
                Nom affiché sur l'annonce <span className="text-xs font-normal text-stone-400">(obligatoire)</span>
              </h3>
              <input
                value={nomAnnonceur}
                onChange={(e) => setNomAnnonceur(e.target.value.slice(0, 40))}
                placeholder="Ex. Edja"
                data-testid="annonce-nom-input"
                className="w-full bg-[#FAF8F5] border border-[#E5DFD5] rounded-2xl px-4 py-3 text-sm font-semibold text-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-300"
              />
            </div>

            <div>
              <h3 className="font-heading font-bold text-stone-900 mb-1">
                Numéros à contacter <span className="text-xs font-normal text-stone-400">(obligatoire)</span>
              </h3>
              <p className="mb-3 text-xs text-stone-500">
                Visibles uniquement par les membres abonnés, avec l'appel direct et WhatsApp.
              </p>
              <div className="space-y-2">
                {telephones.map((num, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="flex flex-1 items-center gap-2 rounded-2xl border border-[#E5DFD5] bg-[#FAF8F5] px-4 py-2.5">
                      <span className="text-sm font-semibold text-stone-500">+225</span>
                      <input
                        value={num}
                        onChange={(e) => setTelephones(telephones.map((t, idx) =>
                          idx === i ? e.target.value.replace(/\D/g, "").slice(0, 10) : t))}
                        inputMode="numeric"
                        placeholder="07 07 07 07 07"
                        data-testid={`annonce-telephone-input-${i}`}
                        className="w-full min-w-0 bg-transparent text-sm font-semibold tracking-wide text-stone-800 outline-none placeholder:font-normal placeholder:text-stone-300"
                      />
                    </span>
                    {telephones.length > 1 && (
                      <button
                        onClick={() => setTelephones(telephones.filter((_, idx) => idx !== i))}
                        data-testid={`annonce-telephone-supprimer-${i}`}
                        aria-label="Retirer ce numéro"
                        className="rounded-full border border-[#E5DFD5] p-2.5 text-stone-400 transition-colors hover:border-red-300 hover:text-red-600"
                      >
                        <X size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {telephones.length < 4 && (
                <button
                  onClick={() => setTelephones([...telephones, ""])}
                  data-testid="annonce-telephone-ajouter"
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#E5DFD5] px-4 py-2 text-xs font-bold text-stone-700 transition-colors hover:border-stone-900"
                >
                  <Plus size={13} /> Ajouter un autre numéro
                </button>
              )}
            </div>

            <div>
              <h3 className="font-heading font-bold text-stone-900 mb-3">Texte de l'annonce <span className="text-xs font-normal text-stone-400">(optionnel)</span></h3>
              <textarea
                value={texte}
                onChange={(e) => setTexte(e.target.value)}
                maxLength={1000}
                rows={5}
                placeholder="Décrivez votre annonce en quelques mots…"
                data-testid="annonce-texte-input"
                className="w-full bg-[#FAF8F5] border border-[#E5DFD5] rounded-2xl px-4 py-3 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-300 resize-none"
              />
              <div className="text-right text-[11px] text-stone-400" data-testid="annonce-texte-compteur">{texte.length}/1000 caractères</div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold text-stone-900">Photos ({photos.length}/{offre.photosMax})</h3>
                <button
                  onClick={() => photoInput.current?.click()}
                  disabled={uploading || photos.length >= offre.photosMax}
                  data-testid="annonce-add-photo"
                  className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border-2 disabled:opacity-40 transition-colors"
                  style={{ borderColor: cat.color, color: cat.color }}
                >
                  {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} Ajouter
                </button>
              </div>
              <input ref={photoInput} type="file" accept="image/*" multiple hidden onChange={onPhotos} data-testid="annonce-photo-input" />
              {photos.length === 0 ? (
                <p className="text-sm text-stone-400">Aucune photo ajoutée.</p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {photos.map((p, i) => (
                    <div key={p.hash + i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-[#E5DFD5]">
                      <img src={mediaUrl(p.url)} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"
                        data-testid={`annonce-remove-photo-${i}`}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {offre.videoMax > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-bold text-stone-900">Vidéo (≤{offre.videoDureeSecondesMax}s)</h3>
                  <button
                    onClick={() => videoInput.current?.click()}
                    disabled={uploading || !!video}
                    data-testid="annonce-add-video"
                    className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border-2 disabled:opacity-40"
                    style={{ borderColor: cat.color, color: cat.color }}
                  >
                    <Video size={15} /> Ajouter
                  </button>
                </div>
                <input ref={videoInput} type="file" accept="video/*" hidden onChange={onVideo} data-testid="annonce-video-input" />
                {video ? (
                  <div className="flex items-center gap-3 text-sm text-stone-600">
                    <video src={mediaUrl(video.url)} className="w-32 rounded-xl border border-[#E5DFD5]" controls />
                    <button onClick={() => setVideo(null)} className="text-red-500" data-testid="annonce-remove-video"><X size={16} /></button>
                  </div>
                ) : (
                  <p className="text-sm text-stone-400">Aucune vidéo ajoutée.</p>
                )}
              </div>
            )}

            <button
              onClick={deposer}
              disabled={submitting || uploading}
              data-testid="annonce-submit"
              className="w-full inline-flex items-center justify-center gap-2 text-white font-semibold py-4 rounded-full transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: cat.color }}
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Megaphone size={16} />}
              {offre.prix === 0 ? "Publier gratuitement" : `Publier — ${formatFCFA(offre.prix)} (Mobile Money)`}
            </button>
          </div>
        )}

        {/* mes annonces */}
        <div className="mt-16" data-testid="mes-annonces">
          <h2 className="font-heading text-2xl font-bold text-stone-900">Mes annonces</h2>
          {annonces.length === 0 ? (
            <p className="text-sm text-stone-500 mt-4">Aucune annonce pour le moment.</p>
          ) : (
            <div className="mt-6 space-y-4">
              {annonces.map((a) => {
                // Les annonces Air-Bengue ont leur propre rubrique : on retombe sur ses couleurs.
                const c = CATEGORIES[a.categorie] || AIR_BENGUE;
                return (
                  <div key={a.id} className="bg-white border border-[#E5DFD5] rounded-2xl p-5 flex flex-wrap items-center gap-4" data-testid={`annonce-item-${a.id}`}>
                    <div className="flex -space-x-3">
                      {a.photos.slice(0, 3).map((p, i) => (
                        <img key={i} src={mediaUrl(p.url)} alt="" className="w-14 h-14 rounded-xl object-cover border-2 border-white" />
                      ))}
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-heading font-bold" style={{ color: c.color }}>{c.name}</div>
                      {a.texte && <p className="text-sm text-stone-600 mt-1 line-clamp-2">{a.texte}</p>}
                      <div className="text-xs text-stone-500 mt-1 flex items-center gap-2">
                        <Clock size={12} />
                        {PALIER_LABELS[a.palier]} · {a.photos.length} photo(s){a.video ? " + vidéo" : ""} · expire le {formatDate(a.date_expiration)}
                      </div>
                    </div>
                    {a.statut === "active" && (
                      <button
                        onClick={() => setEditAnnonce(a)}
                        data-testid={`annonce-modifier-${a.id}`}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border-2 transition-all hover:text-white"
                        style={{ borderColor: c.color, color: c.color }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = c.color)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <Pencil size={14} /> Modifier
                      </button>
                    )}
                    <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${a.statut === "active" ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                      {a.statut === "active" ? "Active" : "Expirée"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      {editAnnonce && tarifs && (
        <EditAnnonceModal
          annonce={editAnnonce}
          offre={tarifs[editAnnonce.categorie][editAnnonce.palier]}
          profileId={user.id}
          onClose={() => setEditAnnonce(null)}
          onSaved={refresh}
        />
      )}
      {controleVisage && (
        <VerificationFaciale
          profileId={user.id}
          categorie={categorie}
          photoReference={controleVisage.photo_reference}
          onFerme={() => setControleVisage(null)}
          onCertifie={() => {
            setControleVisage(null);
            setEtatVisage((e) => (e ? { ...e, certifie: true, obligatoire: false } : e));
            deposer();
          }}
        />
      )}
      <Footer />
    </div>
  );
}
